import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Send, Loader2 } from "lucide-react";
import { BRAND } from "@/lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", mobile: "", email: "", city: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.mobile || !form.city) {
      toast.error("Please fill all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you! Parimal will reach out within 24 hours.");
      setForm({ name: "", mobile: "", email: "", city: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Validation error — check your inputs");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-[#F3F5F4]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 grid lg:grid-cols-12 gap-12">
        {/* Left — info */}
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <span className="inline-flex items-center gap-2 text-sm font-medium text-[#00A65A]">
            <span className="w-6 h-px bg-[#00A65A]" />
            Begin the conversation
          </span>
          <h2 className="mt-3 font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight text-[#0B1B3D] leading-[1.04]">
            Book a complimentary <span className="italic font-light text-[#00A65A]">30-minute</span> call.
          </h2>
          <p className="mt-5 text-[#0B1B3D]/70 leading-relaxed">
            No agenda. No products pushed. Just a candid conversation about where you are and where
            you want to be.
          </p>

          <div className="mt-10 space-y-5">
            <a
              href={BRAND.phoneHref}
              data-testid="contact-phone-link"
              className="flex items-start gap-4 group"
            >
              <span className="w-12 h-12 rounded-2xl bg-white border border-black/10 flex items-center justify-center group-hover:bg-[#0B1B3D] transition-colors">
                <Phone className="w-5 h-5 text-[#00A65A] group-hover:text-white transition-colors" />
              </span>
              <div>
                <div className="font-display text-2xl text-[#0B1B3D] leading-tight">{BRAND.phone}</div>
                <div className="text-xs text-[#0B1B3D]/55 mt-1">Call Parimal directly</div>
              </div>
            </a>

            <a
              href={`mailto:${BRAND.email}`}
              data-testid="contact-email-link"
              className="flex items-start gap-4 group"
            >
              <span className="w-12 h-12 rounded-2xl bg-white border border-black/10 flex items-center justify-center group-hover:bg-[#0B1B3D] transition-colors">
                <Mail className="w-5 h-5 text-[#00A65A] group-hover:text-white transition-colors" />
              </span>
              <div>
                <div className="font-body text-base text-[#0B1B3D] leading-tight">{BRAND.email}</div>
                <div className="text-xs text-[#0B1B3D]/55 mt-1">Email us</div>
              </div>
            </a>

            <div className="flex items-start gap-4">
              <span className="w-12 h-12 rounded-2xl bg-white border border-black/10 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-[#00A65A]" />
              </span>
              <div>
                <div className="font-body text-sm text-[#0B1B3D] max-w-[280px] leading-relaxed">
                  {BRAND.address}
                </div>
                <div className="text-xs text-[#0B1B3D]/55 mt-1">Visit office</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Right — form */}
        <motion.form
          onSubmit={onSubmit}
          data-testid="contact-form"
          initial={{ opacity: 0, x: 24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-7 bg-white rounded-3xl p-8 md:p-10 border border-black/5 shadow-xl"
        >
          <div className="grid md:grid-cols-2 gap-5">
            <Field
              label="Full Name *"
              name="name"
              testid="contact-name"
              value={form.name}
              onChange={onChange}
              placeholder="e.g. Hardik Patel"
            />
            <Field
              label="Mobile Number *"
              name="mobile"
              type="tel"
              testid="contact-mobile"
              value={form.mobile}
              onChange={onChange}
              placeholder="+91 ..."
            />
            <Field
              label="Email *"
              name="email"
              type="email"
              testid="contact-email"
              value={form.email}
              onChange={onChange}
              placeholder="you@example.com"
            />
            <Field
              label="City *"
              name="city"
              testid="contact-city"
              value={form.city}
              onChange={onChange}
              placeholder="Navsari, Surat..."
            />
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-[#0B1B3D]">
                How can Parimal help?
              </label>
              <textarea
                data-testid="contact-message"
                name="message"
                rows={4}
                value={form.message}
                onChange={onChange}
                placeholder="Tell us about your goals — retirement, education, wealth building..."
                className="mt-2 w-full bg-[#F8FAF9] border-0 border-b-2 border-[#0B1B3D]/15 px-3 py-3 focus:border-[#00A65A] focus:bg-white outline-none transition-all rounded-md resize-none font-body text-[#0B1B3D]"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit"
            className="cta-glow mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#0B1B3D] text-white font-medium hover:bg-[#1A2F5B] transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {loading ? "Sending..." : "Request consultation"}
          </button>

          <p className="mt-5 text-xs text-[#0B1B3D]/50">
            By submitting, you agree we may contact you about your enquiry. We never share your
            information.
          </p>
        </motion.form>
      </div>
    </section>
  );
}

function Field({ label, name, type = "text", value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="text-sm font-medium text-[#0B1B3D]">{label}</label>
      <input
        data-testid={testid}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="mt-2 w-full bg-[#F8FAF9] border-0 border-b-2 border-[#0B1B3D]/15 px-3 py-3 focus:border-[#00A65A] focus:bg-white outline-none transition-all rounded-md font-body text-[#0B1B3D]"
      />
    </div>
  );
}
